import { useEffect, useMemo, useState } from "react";
import { useGetAsset } from "@/api/pizza-checker/storage/storage";

interface Asset {
  src: string;
  isLoading: boolean;
}

export interface PizzaResponseData {
  url: string;
}

export function useAsset({
  pizzaId,
  type = "source",
  scale = 100,
  enabled = true,
}: {
  pizzaId: string;
  type?: string;
  scale?: number;
  enabled?: boolean;
}): Asset {
  const { data, isLoading } = useGetAsset<Blob>(
    {
      pizza_id: pizzaId,
      type: type,
      // the backend resizes before encoding: thumbnails cost a fraction of the full photo
      scale,
    },
    {
      request: {
        responseType: "blob",
      },
      query: {
        retry: 0,
        enabled: enabled,
        queryKey: [pizzaId, type, scale],
        staleTime: 10 * 60 * 1000,
      },
    },
  );

  const url = useMemo(() => {
    if (!data) return "";
    return URL.createObjectURL(data);
  }, [data]);

  return { src: url, isLoading };
}

/**
 * A layer asset decoded into an HTMLImageElement (null until loaded / when
 * absent), for canvases that draw the asset themselves.
 */
export function useAssetImage({
  pizzaId,
  type,
  enabled = true,
}: {
  pizzaId: string;
  type: string;
  enabled?: boolean;
}): HTMLImageElement | null {
  const { src } = useAsset({ pizzaId, type, enabled });
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) {
      setImage(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setImage(img);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);
  return image;
}
